import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import { Box, Button, IconButton, Tooltip, Typography } from '@mui/material';
import { AutocompleteCDS, TextFieldCDS } from '@sharedCR/coreDesignSystem/form';
import {
  AiSparkleIcon,
  CancelIcon,
  EditPenIcon,
  PlusIcon,
  ResetIcon,
} from '@sharedCR/coreDesignSystem/icons';
import PropTypes from 'prop-types';

import { formatPriorToDisplayName } from 'constants/condition';
import { IconSize } from 'constants/coreDesignSystem';
import { findPriorToOption, getPriorToPickerOptions } from 'utils/priorTo';

import { selectPriorTo, selectPriorToAllOptions } from 'store/underwriting/selectors';

import {
  CONDITION_REVIEW_LABELS,
  CONDITION_ROW_STATE,
  ConditionReviewTestIds,
  EMPTY_BORROWER_OPTIONS,
  EMPTY_CONDITION_TASKS,
} from '../ConditionReviewList.constants';

import ConditionTaskAssigneeRow from './components/ConditionTaskAssigneeRow';
import {
  actionsRowStyles,
  addBackButtonStyles,
  editBorrowerFieldStyles,
  editFieldsStyles,
  editFooterStyles,
  editPriorToFieldStyles,
  editRowStyles,
  editTasksContainerStyles,
  fieldLabelStyles,
  getItemStyles,
  getLineStyles,
  getTaskLineStyles,
  quietActionButtonStyles,
  removeActionButtonStyles,
  sendBackContainerStyles,
  sendBackHeaderStyles,
  sendBackNoteStyles,
  taskAssigneeStyles,
  tasksContainerStyles,
} from './ConditionReviewItem.styles';
import {
  buildDisplayTasks,
  buildEditedTasks,
  buildInitialTaskAssignees,
  getEditableTasks,
} from './ConditionReviewItem.utils';

const ConditionReviewItem = ({ condition, review, variant }) => {
  const {
    index,
    isLibrary = false,
    tasks = EMPTY_CONDITION_TASKS,
    conditionId,
    borrowerOptions = EMPTY_BORROWER_OPTIONS,
  } = condition;
  const priorToOptions = useSelector(selectPriorTo);
  const allPriorToOptions = useSelector(selectPriorToAllOptions);
  const rowState = review.getRowState(index);
  const savedEdit = review.getEdit(index);

  const isEditing = rowState === CONDITION_ROW_STATE.EDIT;
  const isRemoved = rowState === CONDITION_ROW_STATE.REMOVE;
  const isSentBack = rowState === CONDITION_ROW_STATE.REJECT;

  const displayPriorToOption = useMemo(
    () =>
      findPriorToOption({
        options: allPriorToOptions,
        priorToTypeId: savedEdit?.editedPriorToTypeId ?? condition.priorToTypeId,
        priorToType: savedEdit?.editedPriorToType ?? condition.priorToRaw,
      }),
    [allPriorToOptions, savedEdit, condition.priorToTypeId, condition.priorToRaw],
  );

  const priorToPickerOptions = useMemo(
    () => getPriorToPickerOptions({ options: priorToOptions, currentOption: displayPriorToOption }),
    [priorToOptions, displayPriorToOption],
  );

  const editableTasks = useMemo(() => getEditableTasks(tasks), [tasks]);
  const displayTasks = useMemo(
    () => buildDisplayTasks(tasks, savedEdit?.editedTasks, borrowerOptions),
    [tasks, savedEdit, borrowerOptions],
  );

  const [internalDraft, setInternalDraft] = useState('');
  const [borrowerDraft, setBorrowerDraft] = useState('');
  const [priorToDraft, setPriorToDraft] = useState(null);
  const [taskAssigneeDraft, setTaskAssigneeDraft] = useState({});

  useEffect(() => {
    if (isEditing) {
      setInternalDraft(savedEdit?.editedInternalText ?? condition.internalText ?? '');
      setBorrowerDraft(savedEdit?.editedBorrowerText ?? condition.borrowerText ?? '');
      setPriorToDraft(displayPriorToOption?.value ?? null);
      setTaskAssigneeDraft(buildInitialTaskAssignees(editableTasks, savedEdit?.editedTasks));
    }
  }, [isEditing, savedEdit, condition, editableTasks, displayPriorToOption]);

  const displayInternal = savedEdit?.editedInternalText ?? condition.internalText;
  const displayBorrower = savedEdit?.editedBorrowerText ?? condition.borrowerText;
  const displayPriorToRaw = savedEdit?.editedPriorToType ?? condition.priorToRaw;
  const displayPriorTo =
    displayPriorToOption?.name ??
    (displayPriorToRaw ? formatPriorToDisplayName(displayPriorToRaw) : null);

  const priorToValue = useMemo(
    () => priorToPickerOptions.find(option => option.value === priorToDraft) ?? null,
    [priorToPickerOptions, priorToDraft],
  );

  const onInternalChange = useCallback(event => setInternalDraft(event.target.value), []);
  const onBorrowerChange = useCallback(event => setBorrowerDraft(event.target.value), []);
  const onPriorToChange = useCallback(
    (_event, option) => setPriorToDraft(option?.value ?? null),
    [],
  );
  const onTaskAssigneeChange = useCallback(
    (taskTypeId, option) =>
      setTaskAssigneeDraft(current => ({ ...current, [taskTypeId]: option?.id ?? null })),
    [],
  );

  const onEditClick = useCallback(() => review.beginEdit(index), [review, index]);
  const onSendBackClick = useCallback(() => review.beginSendBack(index), [review, index]);
  const onRemoveClick = useCallback(() => review.remove(index), [review, index]);
  const onAddBackClick = useCallback(() => review.addBack(index), [review, index]);
  const onCancelEdit = useCallback(() => review.cancelEdit(), [review]);

  const onSaveEdit = useCallback(() => {
    const trimmedInternal = internalDraft.trim();
    const trimmedBorrower = borrowerDraft.trim();

    review.saveEdit(index, {
      editedInternalText: trimmedInternal === '' ? null : trimmedInternal,
      editedBorrowerText: trimmedBorrower === '' ? null : trimmedBorrower,
      editedPriorToTypeId: priorToValue?.value ?? null,
      editedPriorToType: priorToValue?.stage ?? null,
      editedTasks: buildEditedTasks(editableTasks, taskAssigneeDraft),
    });
  }, [review, index, internalDraft, borrowerDraft, priorToValue, editableTasks, taskAssigneeDraft]);

  const onSendBackFeedbackChange = useCallback(
    event => review.setSendBackFeedback(index, event.target.value),
    [review, index],
  );

  const onCancelSendBack = useCallback(() => review.cancelSendBack(index), [review, index]);

  const itemStyles = useMemo(
    () => theme => getItemStyles(theme, { variant, rowState }),
    [variant, rowState],
  );
  const lineStyles = useMemo(() => theme => getLineStyles(theme, { isRemoved }), [isRemoved]);
  const taskLineStyles = useMemo(
    () => theme => getTaskLineStyles(theme, { isRemoved }),
    [isRemoved],
  );

  const hasTasks = isLibrary && tasks.length > 0;
  // A library condition whose wording could not be resolved (library not loaded /
  // condition disabled) still gets a reference line so the card is never blank.
  const showReference = isLibrary && !displayInternal && !displayBorrower;

  if (isEditing) {
    return (
      <Box data-testid={ConditionReviewTestIds.ITEM} sx={itemStyles}>
        <Box sx={editFieldsStyles}>
          <TextFieldCDS
            fullWidth
            multiline
            data-testid={ConditionReviewTestIds.EDIT_INTERNAL_INPUT}
            label={CONDITION_REVIEW_LABELS.EDIT_INTERNAL_LABEL}
            minRows={2}
            name={`condition-${index}-internal`}
            size="small"
            value={internalDraft}
            onChange={onInternalChange}
          />
          <Box sx={editRowStyles}>
            <Box sx={editBorrowerFieldStyles}>
              <TextFieldCDS
                fullWidth
                multiline
                data-testid={ConditionReviewTestIds.EDIT_BORROWER_INPUT}
                label={CONDITION_REVIEW_LABELS.EDIT_BORROWER_LABEL}
                minRows={2}
                name={`condition-${index}-borrower`}
                size="small"
                value={borrowerDraft}
                onChange={onBorrowerChange}
              />
            </Box>
            <Box sx={editPriorToFieldStyles}>
              <AutocompleteCDS
                dataTestId={ConditionReviewTestIds.EDIT_PRIOR_TO_SELECT}
                label={CONDITION_REVIEW_LABELS.EDIT_PRIOR_TO_LABEL}
                name={`condition-${index}-priorTo`}
                optionKey="value"
                options={priorToPickerOptions}
                value={priorToValue}
                withSortedOptions={false}
                onChange={onPriorToChange}
              />
            </Box>
          </Box>
        </Box>
        {editableTasks.length > 0 ? (
          <Box data-testid={ConditionReviewTestIds.EDIT_TASKS} sx={editTasksContainerStyles}>
            <Typography sx={fieldLabelStyles} variant="bold12">
              {CONDITION_REVIEW_LABELS.EDIT_TASKS_LABEL}
            </Typography>
            {editableTasks.map(task => (
              <ConditionTaskAssigneeRow
                assigneeId={taskAssigneeDraft[task.taskTypeId]}
                borrowerOptions={borrowerOptions}
                key={task.key}
                task={task}
                onChange={onTaskAssigneeChange}
              />
            ))}
          </Box>
        ) : null}
        <Box sx={editFooterStyles}>
          <Button
            data-testid={ConditionReviewTestIds.EDIT_CANCEL}
            size="small"
            variant="line"
            onClick={onCancelEdit}
          >
            {CONDITION_REVIEW_LABELS.EDIT_CANCEL}
          </Button>
          <Button data-testid={ConditionReviewTestIds.EDIT_SAVE} size="small" onClick={onSaveEdit}>
            {CONDITION_REVIEW_LABELS.EDIT_SAVE}
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box data-testid={ConditionReviewTestIds.ITEM} sx={itemStyles}>
      <Box sx={actionsRowStyles}>
        {isRemoved ? (
          <Tooltip title={CONDITION_REVIEW_LABELS.ADD_BACK}>
            <IconButton
              aria-label={CONDITION_REVIEW_LABELS.ADD_BACK}
              data-testid={ConditionReviewTestIds.ADD_BACK_BUTTON}
              size="small"
              sx={addBackButtonStyles}
              onClick={onAddBackClick}
            >
              <PlusIcon size={IconSize[16]} />
            </IconButton>
          </Tooltip>
        ) : null}
        {isRemoved || isSentBack ? null : (
          <>
            <Tooltip title={CONDITION_REVIEW_LABELS.EDIT}>
              <IconButton
                aria-label={CONDITION_REVIEW_LABELS.EDIT}
                data-testid={ConditionReviewTestIds.EDIT_BUTTON}
                size="small"
                sx={quietActionButtonStyles}
                onClick={onEditClick}
              >
                <EditPenIcon size={IconSize[16]} />
              </IconButton>
            </Tooltip>
            <Tooltip title={CONDITION_REVIEW_LABELS.SEND_BACK}>
              <IconButton
                aria-label={CONDITION_REVIEW_LABELS.SEND_BACK}
                data-testid={ConditionReviewTestIds.SEND_BACK_BUTTON}
                size="small"
                sx={quietActionButtonStyles}
                onClick={onSendBackClick}
              >
                <ResetIcon size={IconSize[16]} />
              </IconButton>
            </Tooltip>
            <Tooltip title={CONDITION_REVIEW_LABELS.REMOVE}>
              <IconButton
                aria-label={CONDITION_REVIEW_LABELS.REMOVE}
                data-testid={ConditionReviewTestIds.REMOVE_BUTTON}
                size="small"
                sx={removeActionButtonStyles}
                onClick={onRemoveClick}
              >
                <CancelIcon size={IconSize[16]} />
              </IconButton>
            </Tooltip>
          </>
        )}
      </Box>
      {showReference ? (
        <Typography
          data-testid={ConditionReviewTestIds.REFERENCE}
          sx={lineStyles}
          variant="regular12"
        >
          {CONDITION_REVIEW_LABELS.CONDITION_REFERENCE({ conditionId })}
        </Typography>
      ) : null}
      {displayInternal ? (
        <Typography sx={lineStyles} variant="regular12">
          <Typography component="span" sx={fieldLabelStyles} variant="bold12">
            {`${CONDITION_REVIEW_LABELS.INTERNAL_LABEL}: `}
          </Typography>
          {displayInternal}
        </Typography>
      ) : null}
      {displayBorrower ? (
        <Typography sx={lineStyles} variant="regular12">
          <Typography component="span" sx={fieldLabelStyles} variant="bold12">
            {`${CONDITION_REVIEW_LABELS.BORROWER_LABEL}: `}
          </Typography>
          {displayBorrower}
        </Typography>
      ) : null}
      {displayPriorTo ? (
        <Typography sx={lineStyles} variant="regular12">
          <Typography component="span" sx={fieldLabelStyles} variant="bold12">
            {`${CONDITION_REVIEW_LABELS.PRIOR_TO_LABEL}: `}
          </Typography>
          {displayPriorTo}
        </Typography>
      ) : null}
      {hasTasks ? (
        <Box data-testid={ConditionReviewTestIds.TASKS} sx={tasksContainerStyles}>
          <Typography sx={fieldLabelStyles} variant="bold12">
            {CONDITION_REVIEW_LABELS.TASKS_LABEL}
          </Typography>
          {displayTasks.map(task => (
            <Typography
              data-testid={ConditionReviewTestIds.TASK_ITEM}
              key={task.key}
              sx={taskLineStyles}
              variant="regular12"
            >
              {task.name ?? CONDITION_REVIEW_LABELS.TASK_FALLBACK}
              {task.assigneeName ? (
                <Typography component="span" sx={taskAssigneeStyles} variant="regular12">
                  {CONDITION_REVIEW_LABELS.TASK_ASSIGNEE(task.assigneeName)}
                </Typography>
              ) : null}
            </Typography>
          ))}
        </Box>
      ) : null}
      {isSentBack ? (
        <Box sx={sendBackContainerStyles}>
          <Typography sx={sendBackHeaderStyles} variant="medium12">
            <AiSparkleIcon size={IconSize[16]} />
            {CONDITION_REVIEW_LABELS.SEND_BACK_HEADER}
          </Typography>
          <TextFieldCDS
            fullWidth
            multiline
            data-testid={ConditionReviewTestIds.SEND_BACK_INPUT}
            minRows={2}
            name={`condition-${index}-sendBack`}
            placeholder={CONDITION_REVIEW_LABELS.SEND_BACK_PLACEHOLDER}
            size="small"
            value={review.getSendBackFeedback(index)}
            onChange={onSendBackFeedbackChange}
          />
          <Typography sx={sendBackNoteStyles} variant="regular12">
            <ResetIcon size={IconSize[16]} />
            {CONDITION_REVIEW_LABELS.SEND_BACK_NOTE}
          </Typography>
          <Box sx={editFooterStyles}>
            <Button
              data-testid={ConditionReviewTestIds.SEND_BACK_CANCEL}
              size="small"
              variant="line"
              onClick={onCancelSendBack}
            >
              {CONDITION_REVIEW_LABELS.SEND_BACK_CANCEL}
            </Button>
          </Box>
        </Box>
      ) : null}
    </Box>
  );
};

ConditionReviewItem.propTypes = {
  condition: PropTypes.shape({
    index: PropTypes.number.isRequired,
    internalText: PropTypes.string,
    borrowerText: PropTypes.string,
    priorTo: PropTypes.string,
    priorToRaw: PropTypes.string,
    priorToTypeId: PropTypes.string,
    isLibrary: PropTypes.bool,
    conditionId: PropTypes.string,
    borrowerOptions: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string,
        name: PropTypes.string,
      }),
    ),
    tasks: PropTypes.arrayOf(
      PropTypes.shape({
        key: PropTypes.string.isRequired,
        name: PropTypes.string,
        assigneeName: PropTypes.string,
        taskTypeId: PropTypes.string,
        assigneeId: PropTypes.string,
        isBorrowerAssigned: PropTypes.bool,
      }),
    ),
  }).isRequired,
  review: PropTypes.object.isRequired,
  variant: PropTypes.string.isRequired,
};

export default memo(ConditionReviewItem);
